import { neutralizeCsvFormula } from '@etiquetachef/types';
import type { ColumnDef } from './types';

const UTF8_BOM = '﻿';

/**
 * Escapa um valor seguindo RFC 4180: campos com vírgula, aspas ou
 * quebra de linha são quoted; aspas internas duplicadas. Sempre
 * retorna string (sem newline final).
 */
export function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  // #795 (2026-10-07, auditoria 2º nível): string que começa como fórmula
  // (`= + - @ TAB CR`) ganha `'` — CSV injection na planilha de quem exporta.
  // Número (tipo `number`) passa intacto.
  const str = typeof value === 'string' ? neutralizeCsvFormula(value) : stringify(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Valor não-string → texto (objetos viram JSON). */
function stringify(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'bigint') return value.toString();
  return JSON.stringify(value);
}

/**
 * Constrói uma string CSV completa em memória. Para tabelas
 * pequenas/médias (até dezenas de milhares de linhas) é o approach
 * mais simples e o ExportButton trabalha em cima dela com Blob +
 * createObjectURL. Maior que isso, considerar `ReadableStream` na
 * próxima iteração.
 *
 * Inclui UTF-8 BOM no início — Excel reconhece BOM como sinal de
 * encoding UTF-8 e renderiza acentos corretamente (sem o BOM,
 * Excel assume Windows-1252 e quebra acentos).
 */
export function toCsv<T>(
  rows: readonly T[],
  columns: readonly ColumnDef<T>[],
): string {
  const headerLine = columns.map((c) => csvEscape(c.header)).join(',');
  const bodyLines = rows.map((row) =>
    columns
      .map((c) => csvEscape(c.csvFormatter ? c.csvFormatter(row) : c.accessor(row)))
      .join(','),
  );
  return UTF8_BOM + [headerLine, ...bodyLines].join('\r\n');
}

/** Embrulha o CSV num `Blob` `text/csv;charset=utf-8`. */
export function csvToBlob(csv: string): Blob {
  return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
}
